import { cn } from '@/utils'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { TOOLTIP_PLACEMENT } from '@/app.constants'

interface CustomTooltipProps {
  placement?: typeof TOOLTIP_PLACEMENT[keyof typeof TOOLTIP_PLACEMENT]
  content: ReactNode
  children: ReactNode
  isVisible?: boolean
  maxWidth?: string | number
  flip?: boolean
  className?: string
  style?: React.CSSProperties
}

const CustomTooltip = ({ placement = TOOLTIP_PLACEMENT.TOP, content, children, isVisible, maxWidth, flip = true, className, style = {}, ...rest }: CustomTooltipProps) => {
  const [tooltipVisible, setTooltipVisible] = useState(isVisible)
  const [tooltipPosition, setTooltipPosition] = useState(placement)
  const targetRef = useRef(null)
  const tooltipRef = useRef(null)

  const adjustTooltipPosition = () => {
    const tooltip = tooltipRef.current as unknown as HTMLElement
    const tooltipRect = tooltip.getBoundingClientRect()
    const { top, left, width, height } = tooltipRect

    // Calculate tooltip boundaries relative to the viewport
    const tooltipTop = top - window.pageYOffset
    const tooltipLeft = left - window.pageXOffset
    const tooltipRight = tooltipLeft + width
    const tooltipBottom = tooltipTop + height

    // Check if tooltip goes beyond the viewport bounds
    if (tooltipLeft < 0) {
      setTooltipPosition(TOOLTIP_PLACEMENT.RIGHT)
    } else if (tooltipRight > window.innerWidth) {
      setTooltipPosition(TOOLTIP_PLACEMENT.LEFT)
    } else if (tooltipTop < 0) {
      setTooltipPosition(TOOLTIP_PLACEMENT.BOTTOM)
    } else if (tooltipBottom > window.innerHeight) {
      setTooltipPosition(TOOLTIP_PLACEMENT.TOP)
    } else {
      setTooltipPosition(placement)
    }
  }

  useEffect(() => {
    if (tooltipVisible && flip) adjustTooltipPosition()
    return () => setTooltipPosition(placement)
  }, [tooltipVisible])

  useEffect(() => {
    if (typeof isVisible !== 'undefined') setTooltipVisible(isVisible)
  }, [isVisible])

  const handleMouseEnter = () => {
    if (typeof isVisible === 'undefined') {
      setTooltipVisible(true)
    }
  }

  const handleMouseLeave = () => {
    if (typeof isVisible === 'undefined') {
      setTooltipVisible(false)
    }
  }

  const tooltipClasses = cn(`absolute w-max-content hidden p-[8px] text-white bg-primary rounded-4px before:content-[''] before:absolute before:w-0 before:h-0 before:border-solid  ${className}`, {
    'block': tooltipVisible,
    'bottom-[calc(100%+8px)] left-1/2 -translate-x-1/2 before:top-full before:left-1/2 before:-translate-x-1/2 before:border-[8px] before:border-transparent before:border-t-primary': tooltipPosition === TOOLTIP_PLACEMENT.TOP,
    'top-[calc(100%+8px)] left-1/2 -translate-x-1/2 before:bottom-full before:left-1/2 before:-translate-x-1/2 before:border-[8px] before:border-transparent before:border-b-primary': tooltipPosition === TOOLTIP_PLACEMENT.BOTTOM,
    'top-1/2 left-[calc(100%+8px)] -translate-y-1/2 before:top-1/2 before:right-[100%] before:-translate-y-1/2 before:border-[8px] before:border-transparent before:border-r-primary': tooltipPosition === TOOLTIP_PLACEMENT.RIGHT,
    'top-1/2 right-[calc(100%+8px)] -translate-y-1/2 before:top-1/2 before:left-[100%] before:-translate-y-1/2 before:border-[8px] before:border-transparent before:border-l-primary': tooltipPosition === TOOLTIP_PLACEMENT.LEFT,
  })

  const tooltipStyle = {
    ...style,
    maxWidth,
    '--tooltip-color': style.color,
    zIndex: 100
  }

  return (
    <div
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={targetRef}
    >
      {children}
      <div ref={tooltipRef} className={tooltipClasses} style={tooltipStyle} {...rest}>
        {content}
      </div>
    </div>
  )
}

export default CustomTooltip
